/**
 * History, the way X keeps it, in the installed iOS app only.
 *
 * iOS draws its own "back" swipe from the left edge and offers no way to turn it off, but it only acts when there is
 * a page to go back to. The phone's drawer is swiped out from the same side, so on a top-level page we keep
 * nothing behind it: moving from one top-level page to another (a tab, a drawer link, a `?tab=` or `?connect=`
 * change on the page itself) replaces the current entry instead of adding one. A detail page (an insight, a thread,
 * a record, a list) is still pushed, so iOS's swipe goes back from it, to the page it was opened from.
 *
 * Android and browser tabs keep the router's own behaviour exactly: `installIosHistory` does nothing there.
 */

/** The top-level pages: the tab bar's four sections, the drawer's other pages and Insights' own tabs. */
export const TOP_LEVEL = [
  "/",
  "/questions",
  "/insights",
  "/insights/goals",
  "/insights/drafts",
  "/insights/playbooks",
  "/lists",
  "/data",
  "/settings",
  "/timeline",
  "/understanding",
] as const;

const tidy = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path) || "/";

export function isTopLevel(pathname: string): boolean {
  return (TOP_LEVEL as readonly string[]).includes(tidy(pathname));
}

/** Pages with no app behind them (sign-in, an invite): leaving one of them should not leave it in the history. */
const BEFORE_APP = ["/sign-in", "/invite"];
const isBeforeApp = (pathname: string) => BEFORE_APP.some((p) => tidy(pathname) === p);

export type Place = { pathname: string; search: string };
export type Step = "push" | "replace" | "back";

/**
 * How a navigation is recorded in the installed iOS app.
 * - `from`: where the person is; `index`: its place in the history (0 is the first entry);
 * - `to`: where they are going; `previous`: the entry just behind the current one, when it is known;
 * - `replace`: the navigation already asked to replace.
 */
export function stepFor(o: {
  from: Place;
  index: number;
  to: Place;
  previous: Place | null;
  replace: boolean;
}): Step {
  const { from, to, previous } = o;
  if (o.replace) return "replace";
  if (isBeforeApp(from.pathname)) return "replace";
  const fromTop = isTopLevel(from.pathname);
  const toTop = isTopLevel(to.pathname);
  // top-level to top-level, or a change on the page itself: nothing is added behind the page
  if (fromTop && toTop) return "replace";
  // a detail page opened from a top-level page, or from another detail page: iOS's swipe goes back to it
  if (!toTop) return "push";
  // a detail page back to a top-level page
  if (previous && tidy(previous.pathname) === tidy(to.pathname) && previous.search === to.search)
    return "back";
  // a deep link opened it: there is nothing behind it, and its parent shouldn't sit on top of it
  if (o.index === 0) return "replace";
  return "push";
}
